/**
 * Emerald Books — Inventory module.
 *
 * Full CRUD:
 *   • Items list, create, edit
 *   • Stock levels view (total value across catalog)
 *   • Manual adjustments with reason + auto-journal
 *   • Item movement ledger drill-down
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Plus, Package, XCircle, Search, Pencil, ArrowUpDown, TrendingUp,
  History, Wrench,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, unwrapList, errMsg, stripEmpty } from './api';

const money = (n) => `${(Number(n) || 0).toFixed(2)}`;
const emptyItem = () => ({
  sku: '', name: '', description: '', unit: 'ea',
  is_service: false, is_tracked: true,
  sale_price: 0, purchase_price: 0,
  sales_account_id: '', expense_account_id: '', inventory_account_id: '',
  opening_qty: 0, opening_value: 0,
});

export const InventoryItemsPage = () => {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyItem());
  const [movModal, setMovModal] = useState(null);
  const [movements, setMovements] = useState([]);
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjust, setAdjust] = useState({ qty_delta: 0, reason: '', at_cost: '', date: new Date().toISOString().slice(0, 10) });

  const load = async () => {
    setLoading(true);
    try {
      const [i, a] = await Promise.all([
        booksClient.get('/inventory/items'),
        booksClient.get('/ledger/accounts'),
      ]);
      setItems(i.data); setAccounts(unwrapList(a));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) => [i.sku, i.name, i.description].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  }, [items, q]);

  const incomeAccounts = useMemo(() => accounts.filter((a) => a.type === 'income'), [accounts]);
  const expenseAccounts = useMemo(() => accounts.filter((a) => a.type === 'expense'), [accounts]);
  const assetAccounts = useMemo(() => accounts.filter((a) => a.type === 'asset'), [accounts]);

  const openCreate = () => { setEditing(null); setForm(emptyItem()); setShowForm(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      ...emptyItem(),
      sku: it.sku, name: it.name, description: it.description || '',
      unit: it.unit || 'ea', is_service: !!it.is_service, is_tracked: !!it.is_tracked,
      sale_price: it.sale_price || 0, purchase_price: it.purchase_price || 0,
      sales_account_id: it.sales_account_id || '',
      expense_account_id: it.expense_account_id || '',
      inventory_account_id: it.inventory_account_id || '',
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const patch = stripEmpty({
          name: form.name, description: form.description, unit: form.unit,
          sale_price: form.sale_price, purchase_price: form.purchase_price,
          sales_account_id: form.sales_account_id,
          expense_account_id: form.expense_account_id,
          inventory_account_id: form.inventory_account_id,
        });
        await booksClient.patch(`/inventory/items/${editing.item_id}`, patch);
        toast.success('Item updated');
      } else {
        const payload = stripEmpty(form);
        // ensure numeric fields present (0 is valid)
        payload.sale_price = form.sale_price || 0;
        payload.purchase_price = form.purchase_price || 0;
        payload.is_service = !!form.is_service;
        payload.is_tracked = form.is_service ? false : true;
        if (!form.is_service) {
          payload.opening_qty = form.opening_qty || 0;
          payload.opening_value = form.opening_value || 0;
        }
        await booksClient.post('/inventory/items', payload);
        toast.success('Item created');
      }
      setShowForm(false);
      setForm(emptyItem());
      setEditing(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openMovements = async (it) => {
    setMovModal(it);
    try {
      const { data } = await booksClient.get(`/inventory/items/${it.item_id}/movements`);
      setMovements(data);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    if (!adjustModal) return;
    try {
      await booksClient.post('/inventory/adjust', {
        item_id: adjustModal.item_id,
        qty_delta: parseFloat(adjust.qty_delta) || 0,
        reason: adjust.reason,
        at_cost: adjust.at_cost ? parseFloat(adjust.at_cost) : null,
        date: adjust.date,
      });
      toast.success('Stock adjusted');
      setAdjustModal(null);
      setAdjust({ qty_delta: 0, reason: '', at_cost: '', date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="mx-auto max-w-6xl" data-testid="books-inventory-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Items & Stock</h1>
          <p className="mt-1 text-sm text-slate-500">Catalog + on-hand quantities + weighted-average cost. Adjustments post journals automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Search SKU or name…" className="pl-7" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inv-search" />
          </div>
          <Button onClick={openCreate} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="inv-new-btn">
            <Plus className="mr-1 h-4 w-4" /> New item
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-6 rounded-xl border bg-white p-5 shadow-sm" data-testid="inv-form">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">SKU</div>
              <Input required disabled={!!editing} value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="inv-sku" />
            </label>
            <label className="text-sm md:col-span-2">
              <div className="mb-1 text-xs text-slate-500">Name</div>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inv-name" />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Unit</div>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="ea, kg, hrs…" />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Sale price</div>
              <Input type="number" step="0.01" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: parseFloat(e.target.value) || 0 })} data-testid="inv-sale" />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Purchase price</div>
              <Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: parseFloat(e.target.value) || 0 })} />
            </label>
            <label className="text-sm md:col-span-2">
              <div className="mb-1 text-xs text-slate-500">Sales income account</div>
              <select value={form.sales_account_id} onChange={(e) => setForm({ ...form, sales_account_id: e.target.value })} className="w-full rounded-md border px-2 py-1.5 text-sm">
                <option value="">— optional —</option>
                {incomeAccounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} · {a.name}</option>)}
              </select>
            </label>
            <label className="text-sm md:col-span-2">
              <div className="mb-1 text-xs text-slate-500">Purchase / COGS expense account</div>
              <select value={form.expense_account_id} onChange={(e) => setForm({ ...form, expense_account_id: e.target.value })} className="w-full rounded-md border px-2 py-1.5 text-sm">
                <option value="">— optional —</option>
                {expenseAccounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} · {a.name}</option>)}
              </select>
            </label>
            <label className="text-sm md:col-span-2">
              <div className="mb-1 text-xs text-slate-500">Inventory asset account</div>
              <select value={form.inventory_account_id} onChange={(e) => setForm({ ...form, inventory_account_id: e.target.value })} className="w-full rounded-md border px-2 py-1.5 text-sm">
                <option value="">— optional —</option>
                {assetAccounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} · {a.name}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_service} onChange={(e) => setForm({ ...form, is_service: e.target.checked, is_tracked: !e.target.checked && form.is_tracked })} disabled={!!editing} />
              <span>Service (no stock tracking)</span>
            </label>
            {!editing && !form.is_service && (
              <>
                <label className="text-sm">
                  <div className="mb-1 text-xs text-slate-500">Opening qty</div>
                  <Input type="number" step="0.01" value={form.opening_qty} onChange={(e) => setForm({ ...form, opening_qty: parseFloat(e.target.value) || 0 })} />
                </label>
                <label className="text-sm">
                  <div className="mb-1 text-xs text-slate-500">Opening value (cost)</div>
                  <Input type="number" step="0.01" value={form.opening_value} onChange={(e) => setForm({ ...form, opening_value: parseFloat(e.target.value) || 0 })} />
                </label>
              </>
            )}
            <label className="text-sm md:col-span-4">
              <div className="mb-1 text-xs text-slate-500">Description</div>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
            <Button type="submit" className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="inv-save">Save</Button>
          </div>
        </form>
      )}

      <div className="rounded-xl border bg-white">
        {loading ? <div className="p-6 text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div> :
          filtered.length === 0 ? <div className="p-10 text-center text-slate-400">No items yet. Click "New item" to add your first.</div> :
          <table className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">SKU</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Kind</th>
                <th className="p-3 text-right">On hand</th>
                <th className="p-3 text-right">Avg cost</th>
                <th className="p-3 text-right">Sale price</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.item_id} className="border-b last:border-b-0" data-testid={`inv-row-${it.sku}`}>
                  <td className="p-3 font-mono text-xs">{it.sku}</td>
                  <td className="p-3">
                    <div className="font-medium">{it.name}</div>
                    {it.description && <div className="text-xs text-slate-400">{it.description}</div>}
                  </td>
                  <td className="p-3 text-xs">
                    {it.is_service ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">service</span>
                                    : <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700">stock</span>}
                  </td>
                  <td className="p-3 text-right">{it.is_service ? '—' : Number(it.on_hand_qty || 0).toFixed(2)}</td>
                  <td className="p-3 text-right">{it.is_service ? '—' : money(it.avg_cost)}</td>
                  <td className="p-3 text-right">{money(it.sale_price)}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => openEdit(it)} className="rounded p-1 text-slate-500 hover:text-emerald-600" title="Edit" data-testid={`inv-edit-${it.sku}`}><Pencil className="h-3.5 w-3.5" /></button>
                      {!it.is_service && <>
                        <button onClick={() => openMovements(it)} className="rounded p-1 text-slate-500 hover:text-emerald-600" title="Movements" data-testid={`inv-movements-${it.sku}`}><History className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setAdjustModal(it)} className="rounded p-1 text-slate-500 hover:text-emerald-600" title="Adjust" data-testid={`inv-adjust-${it.sku}`}><Wrench className="h-3.5 w-3.5" /></button>
                      </>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>

      {/* Movement modal */}
      {movModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="inv-movements-modal">
          <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Movements — {movModal.name}</h2>
                <div className="text-xs text-slate-400 font-mono">{movModal.sku}</div>
              </div>
              <button onClick={() => setMovModal(null)} className="text-slate-400 hover:text-slate-700"><XCircle className="h-5 w-5" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto rounded border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="p-3 text-left">Date</th>
                    <th className="p-3 text-left">Kind</th>
                    <th className="p-3 text-right">Qty Δ</th>
                    <th className="p-3 text-right">Unit cost</th>
                    <th className="p-3 text-right">Bal qty</th>
                    <th className="p-3 text-right">Bal avg cost</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">No movements.</td></tr>}
                  {movements.map((m) => (
                    <tr key={m.movement_id} className="border-b last:border-b-0">
                      <td className="p-3">{m.date}</td>
                      <td className="p-3 text-xs uppercase text-slate-500">{m.kind}{m.reason ? ` · ${m.reason}` : ''}</td>
                      <td className={'p-3 text-right ' + (m.qty_delta < 0 ? 'text-rose-600' : 'text-emerald-700')}>{Number(m.qty_delta).toFixed(2)}</td>
                      <td className="p-3 text-right">{money(m.unit_cost)}</td>
                      <td className="p-3 text-right">{Number(m.balance_qty).toFixed(2)}</td>
                      <td className="p-3 text-right">{money(m.balance_avg_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setMovModal(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Adjust modal */}
      {adjustModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="inv-adjust-modal">
          <form onSubmit={submitAdjust} className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Adjust — {adjustModal.name}</h2>
              <button type="button" onClick={() => setAdjustModal(null)} className="text-slate-400 hover:text-slate-700"><XCircle className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <label className="block">
                <div className="mb-1 text-xs text-slate-500">Qty change (+ / −)</div>
                <Input type="number" step="0.01" required value={adjust.qty_delta} onChange={(e) => setAdjust({ ...adjust, qty_delta: e.target.value })} data-testid="adjust-qty" />
              </label>
              <label className="block">
                <div className="mb-1 text-xs text-slate-500">At cost (per unit, required for +ve)</div>
                <Input type="number" step="0.01" value={adjust.at_cost} onChange={(e) => setAdjust({ ...adjust, at_cost: e.target.value })} placeholder={money(adjustModal.avg_cost)} />
              </label>
              <label className="block">
                <div className="mb-1 text-xs text-slate-500">Reason</div>
                <Input required value={adjust.reason} onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })} data-testid="adjust-reason" />
              </label>
              <label className="block">
                <div className="mb-1 text-xs text-slate-500">Date</div>
                <Input type="date" required value={adjust.date} onChange={(e) => setAdjust({ ...adjust, date: e.target.value })} />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAdjustModal(null)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="adjust-save">Adjust stock</Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Stock Levels                                                                */
/* -------------------------------------------------------------------------- */
export const StockLevelsPage = () => {
  const [data, setData] = useState({ items: [], total_stock_value: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await booksClient.get('/inventory/stock-levels'); setData(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="mx-auto max-w-4xl" data-testid="books-stock-levels-page">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight">Stock Levels</h1>
        <p className="mt-1 text-sm text-slate-500">Snapshot of on-hand quantity and value across tracked items.</p>
      </div>
      <div className="mb-4 rounded-xl border bg-emerald-50/70 p-4 flex items-center justify-between">
        <div className="text-sm text-emerald-800">Total inventory value</div>
        <div className="text-2xl font-semibold text-emerald-700">{money(data.total_stock_value)}</div>
      </div>
      <div className="rounded-xl border bg-white">
        {loading ? <div className="p-6 text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div> :
          data.items.length === 0 ? <div className="p-10 text-center text-slate-400">No tracked items.</div> :
          <table className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">SKU</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-right">On hand</th>
                <th className="p-3 text-right">Avg cost</th>
                <th className="p-3 text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.item_id} className="border-b last:border-b-0">
                  <td className="p-3 font-mono text-xs">{it.sku}</td>
                  <td className="p-3">{it.name}</td>
                  <td className="p-3 text-right">{Number(it.on_hand_qty).toFixed(2)} {it.unit}</td>
                  <td className="p-3 text-right">{money(it.avg_cost)}</td>
                  <td className="p-3 text-right font-medium">{money(it.stock_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
};
